'use client'

import { useTranslations } from 'next-intl'
import { ChangeEvent, useState } from 'react'
import { GravelOrder, parseGravelOrder } from '@/types/orders'
import { ApiAddressSuggestion } from '@/types/address-suggestions'
import { flattenParseErrors } from '@/lib/utils'
import { Input } from '@/app/[locale]/(ui)/input'
import { AddressInput } from '@/app/[locale]/(ui)/address-input'
import { Textarea } from '@/app/[locale]/(ui)/textarea'
import { Alert } from '@/app/[locale]/(ui)/alert'
import { TermsAndConditions } from '@/app/[locale]/(common)/terms-and-conditions'
import { Button } from '@/app/[locale]/(ui)/button'
import { ArrowRight } from 'lucide-react'
import { Radio } from '@/app/[locale]/(ui)/radio'
import { DateInput } from '@/app/[locale]/(ui)/date-input'
import { trackOrderCompleted } from '@/lib/tracking'
import { createGravelOrder } from '@/lib/orders'
import { formatAddressSuggestion } from '@/lib/address-suggestions'
import { GravelPriceTable } from '@/app/[locale]/(shop)/bestill-pukk/gravel-price-table'
import { toOsloDateString } from '@/lib/pickup-date'

export const GravelOrderForm = () => {
  const t = useTranslations()
  const [hasError, setHasError] = useState(false)
  const [hasSuccess, setHasSuccess] = useState(false)
  const [isValidAddress, setIsValidAddress] = useState(false)
  const [isTermsChecked, setIsTermsChecked] = useState(false)
  const [isBusinessCustomer, setIsBusinessCustomer] = useState(false)
  const [order, setOrder] = useState<Partial<GravelOrder>>({})
  const [validationErrors, setValidationErrors] = useState<any>({})
  const hasValidationErrors = Object.keys(validationErrors).length > 0 || (order.deliveryAddress && !isValidAddress)
  const importantToKnow = t.raw('gravelPage.importantToKnow.items') as string[]

  const onChange = (name: keyof GravelOrder) => (event: ChangeEvent<any>) =>
    setOrder((prev) => ({ ...prev, [name]: event.target.value }))

  const onAddressChange = (address: ApiAddressSuggestion) => {
    setOrder((prev) => ({ ...prev, deliveryAddress: formatAddressSuggestion(address) }))
  }

  const onIsBusinessCustomerChange = (isBusinessCustomer: boolean) => {
    setIsBusinessCustomer(isBusinessCustomer)
    if (!isBusinessCustomer)
      setOrder((prev) => {
        const { orgNumber, orgName, ...rest } = prev
        return rest
      })
  }

  const onSubmit = async () => {
    const newOrder = parseGravelOrder(order, isBusinessCustomer)
    setValidationErrors(flattenParseErrors(newOrder.error))
    if (newOrder.error) return

    try {
      await createGravelOrder(newOrder.data)
      setHasSuccess(true)
      setOrder({})
      setValidationErrors({})
      setIsTermsChecked(false)
      trackOrderCompleted({
        cart: [],
        total: { gross: 0 },
        customerType: isBusinessCustomer ? 'business' : 'private',
        orderType: 'gravel',
        origin: 'form',
      })
    } catch {
      return setHasError(true)
    }
  }

  return (
    <div className="my-12 bg-gray-100 p-8 sm:rounded-2xl md:my-16">
      <h1 className="mb-8 text-3xl font-semibold">{t('Gravel_order_form_title')}</h1>
      <div className="space-y-2">
        <GravelPriceTable />
      </div>
      <div className="mb-4 mt-6 flex gap-4">
        <span className="font-bold">Velg kundetype:</span>
        <Radio label="Privat" checked={!isBusinessCustomer} onChange={() => onIsBusinessCustomerChange(false)} />
        <Radio label="Bedrift" checked={isBusinessCustomer} onChange={() => onIsBusinessCustomerChange(true)} />
      </div>
      <div className="mt-5 flex flex-col gap-3">
        {isBusinessCustomer && (
          <>
            <Input
              label={t('Organization_number')}
              placeholder={t('Organization_number')}
              value={order.orgNumber}
              error={validationErrors.orgNumber}
              onChange={onChange('orgNumber')}
              required
            />
            <Input
              label={t('Company_name')}
              placeholder={t('Company_name')}
              value={order.orgName}
              error={validationErrors.orgName}
              onChange={onChange('orgName')}
              required
            />
          </>
        )}
        <Input
          label={t('Name')}
          placeholder={t('Name')}
          value={order.name}
          error={validationErrors.name}
          onChange={onChange('name')}
          required
        />
        <Input
          label={t('Email')}
          placeholder={t('Email')}
          value={order.email}
          error={validationErrors.email}
          onChange={onChange('email')}
          required
        />
        <Input
          label={t('Phone_number')}
          placeholder={t('Phone_number')}
          value={order.phoneNumber}
          error={validationErrors.phoneNumber}
          onChange={onChange('phoneNumber')}
          required
        />
        <Input
          label={t('Invoice_address')}
          placeholder={t('Invoice_address')}
          value={order.invoiceAddress}
          error={validationErrors.invoiceAddress}
          onChange={onChange('invoiceAddress')}
          required
        />
        <AddressInput
          isValidAddress={isValidAddress}
          setIsValidAddress={setIsValidAddress}
          whenAddressIsSelected={onAddressChange}
          error={validationErrors.deliveryAddress}
          placeholder={t('Assignment_address_label')}
          label={t('Assignment_address_label')}
          required
        />
        <DateInput
          date={order.deliveryDate ? new Date(order.deliveryDate) : undefined}
          label={t('Delivery_date')}
          onChange={(date) => setOrder((prev) => ({ ...prev, deliveryDate: toOsloDateString(date) }))}
        />
        <Textarea
          label={t('Order')}
          placeholder={t('Order_placeholder')}
          value={order.orderText}
          error={validationErrors.orderText}
          onChange={onChange('orderText')}
          required
        />
        <ul className="my-2 list-disc pl-5 text-sm">
          {importantToKnow.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
        <TermsAndConditions simple checked={isTermsChecked} onChange={setIsTermsChecked} />
        {hasSuccess && (
          <Alert variant="success" className="mt-4">
            {t('We_have_received_your_order')}
          </Alert>
        )}
        {hasError && (
          <Alert variant="error" className="mt-4">
            {t('Error_sending_order')}
          </Alert>
        )}
        {hasValidationErrors && <Alert variant="error">{t('Form_missing_fields')}</Alert>}
        <Button variant="primary" className="w-full" disabled={!isTermsChecked} onClick={onSubmit}>
          {t('Submit')}
          <ArrowRight size={20} />
        </Button>
      </div>
    </div>
  )
}
